import { useCallback, useMemo } from 'react';
import { teamApi, useGetStatusesQuery, UserStatus } from '../store/features/Team/teamService';
import { mergeStatuses } from '../store/features/Team/statusesLongPoll';
import { useStatusesLongPoll } from './useStatusesLongPoll';

interface UseUserStatusesOptions {
    userIds?: (string | number)[];
    skip?: boolean;
}

interface UseUserStatusesResult {
    statuses: UserStatus[];
    getStatus: (userId: string | number | undefined) => UserStatus | undefined;
    isLoading: boolean;
    isError: boolean;
}

export const useUserStatuses = (options: UseUserStatusesOptions = {}): UseUserStatusesResult => {
    const { userIds = [], skip = false } = options;

    const validUserIds = useMemo(() => {
        return userIds
            .map(id => String(id))
            .filter(id => id && id.trim() !== '' && id !== 'undefined' && id !== 'null');
    }, [userIds]);

    const skipQuery = skip || validUserIds.length === 0;
    const { data: statuses = [], isLoading, isError } = useGetStatusesQuery(
        { users: validUserIds },
        { skip: skipQuery }
    );
    useStatusesLongPoll({
        users: validUserIds,
        update: (incoming) => teamApi.util.updateQueryData('getStatuses', { users: validUserIds }, mergeStatuses(incoming)),
        skip: skipQuery,
    });

    const getStatus = useCallback((userId: string | number | undefined): UserStatus | undefined => {
        if (!userId && userId !== 0) return undefined;
        return statuses.find(status => String(status.userId) === String(userId));
    }, [statuses]);

    return {
        statuses,
        getStatus,
        isLoading,
        isError
    };
};

export default useUserStatuses;
